import { useState, useCallback } from 'react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import { Project } from '../types/project';
import toast from 'react-hot-toast';

export const useSoundworldProjects = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadProjects = useCallback(async (attempt = 1) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_PROJECTS}`, {
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                if (res.status === 401) {
                    throw new Error("Authentication required");
                }
                throw new Error("Failed to load soundworld projects");
            }

            const data = await res.json();
            setProjects(data);
            setError(null);
        } catch (err) {
            if (attempt < 3) {
                setTimeout(() => {
                    loadProjects(attempt + 1);
                }, 400);
            } else {
                setError(err instanceof Error ? err.message : "Failed to load soundworld projects");
            }
        } finally {
            setIsLoading(false);
        }
    }, []);

    const deleteProject = useCallback(async (projectId: string) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_DELETE_PROJECT}?project_id=${projectId}`, {
                method: 'DELETE',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error("Failed to delete project");
            }

            // Remove project from local state
            setProjects(prev => prev.filter(project => project.project_id !== projectId));
            toast.success("Project deleted successfully");
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to delete project";
            toast.error(errorMessage);
            return false;
        }
    }, []);

    const refreshProjects = useCallback(() => {
        loadProjects();
    }, [loadProjects]);

    return {
        projects,
        isLoading,
        error,
        loadProjects,
        deleteProject,
        refreshProjects,
    };
}; 